import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { LoadingSpinner } from '@/components/common';
import { formatCurrency } from '@/utils';
import axios from 'axios';

interface InsurerProfile {
  id: string;
  name: string;
  short_name: string;
  logo_url: string;
  description: string;
  website: string;
  phone: string;
  email: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  pincode: string;
  public_profile_slug: string;
  category: string;
  claim_settlement_ratio: string | null;
  customer_rating: string | null;
  products_count: number;
}

interface InsuranceProduct {
  id: string;
  name: string;
  short_name: string;
  category: string;
  category_display: string;
  description: string;
  key_features: string[];
  coverage_details: Record<string, unknown>;
  exclusions: string;
  brochure_pdf: string | null;
  policy_wording_pdf: string | null;
  claim_form_pdf: string | null;
  min_premium: string | null;
  max_premium: string | null;
  premium_frequency: string;
}

export function InsuranceCompanyProfile() {
  const { slug } = useParams<{ slug: string }>();
  const [company, setCompany] = useState<InsurerProfile | null>(null);
  const [products, setProducts] = useState<InsuranceProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;

    const loadProfile = async () => {
      try {
        setLoading(true);
        const [companyRes, productsRes] = await Promise.all([
          axios.get(`/policy/api/public/insurers/${slug}/`),
          axios.get(`/policy/api/public/insurers/${slug}/products/`),
        ]);
        setCompany(companyRes.data);
        setProducts(productsRes.data);
      } catch {
        setError('Company not found');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Company Not Found</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6">The insurance company profile you&apos;re looking for doesn&apos;t exist.</p>
        <Link to="/insurers" className="text-blue-600 hover:text-blue-800 dark:text-blue-400">
          Browse all insurers
        </Link>
      </div>
    );
  }

  const address = [company.address_line1, company.address_line2, company.city, company.state, company.pincode]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-6">
            {company.logo_url && (
              <img
                src={company.logo_url}
                alt={company.name}
                className="h-20 w-20 rounded-lg object-contain bg-gray-100 dark:bg-gray-700 p-2"
              />
            )}
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{company.name}</h1>
              {company.short_name && (
                <p className="text-lg text-gray-500 dark:text-gray-400">{company.short_name}</p>
              )}
              <div className="flex items-center gap-4 mt-2">
                <span className="inline-flex items-center rounded-full bg-blue-100 dark:bg-blue-900 px-3 py-0.5 text-sm font-medium text-blue-800 dark:text-blue-200 capitalize">
                  {company.category}
                </span>
                {company.customer_rating && (
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Rating: {company.customer_rating}/5
                  </span>
                )}
                {company.claim_settlement_ratio && (
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Claim Settlement: {company.claim_settlement_ratio}%
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* About */}
        {company.description && (
          <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">About</h2>
            <p className="text-gray-700 dark:text-gray-300">{company.description}</p>
          </section>
        )}

        {/* Contact Info */}
        <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Contact Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {company.website && (
              <div>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Website</span>
                <a href={company.website} target="_blank" rel="noopener noreferrer" className="block text-blue-600 dark:text-blue-400 hover:underline">
                  {company.website}
                </a>
              </div>
            )}
            {company.phone && (
              <div>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Phone</span>
                <p className="text-gray-900 dark:text-white">{company.phone}</p>
              </div>
            )}
            {company.email && (
              <div>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Email</span>
                <a href={`mailto:${company.email}`} className="block text-blue-600 dark:text-blue-400 hover:underline">
                  {company.email}
                </a>
              </div>
            )}
            {address && (
              <div>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Address</span>
                <p className="text-gray-900 dark:text-white">{address}</p>
              </div>
            )}
          </div>
        </section>

        {/* Products */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Insurance Products ({products.length})
          </h2>

          {products.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
              <p className="text-gray-500 dark:text-gray-400">No public products available.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product) => (
                <div key={product.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <span className="inline-flex items-center rounded-full bg-green-100 dark:bg-green-900 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:text-green-200">
                      {product.category_display}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{product.name}</h3>
                  {product.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-3">{product.description}</p>
                  )}

                  {product.key_features && product.key_features.length > 0 && (
                    <div className="mb-3">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Key Features</h4>
                      <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-0.5">
                        {product.key_features.slice(0, 5).map((feature, idx) => (
                          <li key={idx}>{feature}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Documents */}
                  <div className="flex flex-wrap gap-2 mt-auto pt-3">
                    {product.brochure_pdf && (
                      <a
                        href={product.brochure_pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 rounded px-2 py-1 hover:bg-blue-100 dark:hover:bg-blue-900/50"
                      >
                        Brochure
                      </a>
                    )}
                    {product.policy_wording_pdf && (
                      <a
                        href={product.policy_wording_pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 rounded px-2 py-1 hover:bg-blue-100 dark:hover:bg-blue-900/50"
                      >
                        Policy Details
                      </a>
                    )}
                    {product.claim_form_pdf && (
                      <a
                        href={product.claim_form_pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 rounded px-2 py-1 hover:bg-blue-100 dark:hover:bg-blue-900/50"
                      >
                        Claim Form
                      </a>
                    )}
                  </div>

                  {(product.min_premium || product.max_premium) && (
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Premium: </span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {product.min_premium && formatCurrency(product.min_premium)}
                        {product.min_premium && product.max_premium && ' - '}
                        {product.max_premium && formatCurrency(product.max_premium)}
                        {product.premium_frequency && ` (${product.premium_frequency})`}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Powered by base.Policy.online
          </p>
        </div>
      </footer>
    </div>
  );
}
